/** Calendar date in `YYYY-MM-DD` form. Timezone-free. */
export type ISODate = string;

/** JavaScript weekday convention: 0 = Sunday … 6 = Saturday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const DEFAULT_WORKING_DAYS: Weekday[] = [1, 2, 3, 4, 5];

export const JOB_STATUSES = [
  "quoted",
  "scheduled",
  "in_progress",
  "at_post_process",
  "complete",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** Statuses whose machining hours consume shop capacity (decision D6). */
export const COMMITTED_STATUSES: readonly JobStatus[] = ["scheduled", "in_progress"];

/** Statuses for which work has already begun (decision D9). */
export const STARTED_STATUSES: readonly JobStatus[] = ["in_progress", "at_post_process", "complete"];

export const RISK_LEVELS = ["ok", "tight", "at_risk", "impossible"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

/** A machine in the shop and how many hours it runs in a week (decision D49). */
export interface Machine {
  id: string;
  weeklyHours: number;
}

/** Part of a machining step's hours, on one machine. */
export interface MachineHours {
  machineId: string;
  hours: number;
}

/** Machining on one or more machines at once. The step's hours are the sum of `machines`. */
export interface MachiningStep {
  type: "machining";
  machines: readonly MachineHours[];
}

export interface PostProcessStep {
  type: "post_process";
  name: string;
  /** Vendor lead time, in calendar days. */
  leadDays: number;
  /** Transit to the vendor, in working days. */
  transitDaysOut: number;
  /** Transit back from the vendor, in working days. */
  transitDaysBack: number;
}

export type JobStep = MachiningStep | PostProcessStep;

export interface EngineSettings {
  workingDays: readonly number[];
  machines: readonly Machine[];
  alertLeadDays: number;
}

export interface DateWindow {
  start: ISODate;
  end: ISODate;
}

/** Hours consumed on one machine on one working day. */
export interface DailyAllocation {
  date: ISODate;
  machineId: string;
  hours: number;
}

/** A machining window already scheduled for another job, with exactly what it uses per machine per day. */
export interface MachiningWindow extends DateWindow {
  hours: number;
  allocations: DailyAllocation[];
}

export interface CommittedJob {
  id: string;
  machining: MachiningWindow[];
}

export interface MachiningSegment extends DateWindow {
  kind: "machining";
  stepIndex: number;
  hours: number;
  /** Number of working days this segment occupies. */
  workingDays: number;
  /** The hours asked of each machine, one entry per machine, whether or not they could be placed. */
  machines: MachineHours[];
  /** Exactly how many hours land on each machine on each working day, oldest first. */
  allocations: DailyAllocation[];
}

export interface PostProcessSegment extends DateWindow {
  kind: "post_process";
  stepIndex: number;
  name: string;
  /** Dispatch to vendor → arrival at vendor. */
  transitOut: DateWindow;
  /** At the vendor. */
  lead: DateWindow;
  /** Dispatch from vendor → back in the shop. */
  transitBack: DateWindow;
}

export type TimelineSegment = MachiningSegment | PostProcessSegment;

export interface ScheduleInput {
  steps: readonly JobStep[];
  dueDate: ISODate;
  settings: EngineSettings;
  committedJobs: readonly CommittedJob[];
  today: ISODate;
  /**
   * The job's status, which decides how risk is judged (decision D9):
   * not started (`quoted`, `scheduled`, the default) → bands against the latest start date;
   * started (`in_progress`, `at_post_process`) → `ok` unless the due date has passed;
   * `complete` → always `ok`. The timeline is computed regardless.
   */
  status?: JobStatus;
}

export interface ScheduleResult {
  latestStartDate: ISODate;
  /** The due date rolled back to the last working day on or before it. */
  effectiveDueDate: ISODate;
  timeline: TimelineSegment[];
  /** Calendar days from latest start to due date, inclusive. */
  totalCalendarDays: number;
  /** Working days between today and the latest start date. Negative when the start date has passed. */
  slackWorkingDays: number;
  /** False when the shop has no capacity that could ever satisfy the machining hours. */
  feasible: boolean;
  riskLevel: RiskLevel;
}

export interface SchedulableJob {
  id: string;
  steps: readonly JobStep[];
  dueDate: ISODate;
  status: JobStatus;
}
